import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Breadcrumb from '../common/Breadcrumb';
import { Switch } from '@headlessui/react';
import { usePaymentTerms } from '../../context/PaymentTermContext';
import { useRoutePath } from '../../utils/getRoutePath';
import { useLoading } from '../../context/LoadingContext';
  
const schema = yup.object().shape({
  name: yup.string().required('Payment term name is required'),
  due_in_days: yup.number()
    .required('Due in days is required')
    .min(0, 'Due in days must be a positive number')
    .integer('Due in days must be a whole number'),
  active: yup.boolean().default(true)
});

const PaymentTermForm = () => {
  const location = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { addPaymentTerm, updatePaymentTerm, getPaymentTermById } = usePaymentTerms();
  const [isLoading, setIsLoading] = useState(true);
  const [isActive, setIsActive] = useState(true);
  const getRoutePath = useRoutePath();
  const initialized = location.pathname === getRoutePath('/payment-terms');
  const ranOnceRef2 = useRef(false);
  const { showLoading, hideLoading } = useLoading();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
     
  } = useForm({
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    if(!id){
      reset({
        name:'',
        due_in_days:0,
        active:true
      }); 
    }
  }, [id,reset]);

  // Load payment term data
  useEffect(() => {
    const loadData = async () => {
      try {
        showLoading()
         
        if (id) {
         
          const termResponse = await getPaymentTermById(id);
          setIsLoading(true)
        
          if (termResponse.status === 200) {
            let  term = termResponse.data;
            
            const formData = {
              name: term.name,
              due_in_days: term.due_in_days || 0,
              active: term.active
            };
            setIsActive(term.active);
            reset(formData);
          } else {
            toast.error('Payment term not found');
            navigate(getRoutePath('/payment-terms'));
          }
          hideLoading()
          setIsLoading(false)
        }
      } catch (error) {
        setIsLoading(false)
        hideLoading()
        console.error('Error loading data:', error);
        toast.error('Error loading payment term data');
      } finally {
        setIsLoading(false)
        hideLoading()
        
      }
    };
    if (ranOnceRef2 && !ranOnceRef2.current) {
      loadData();
      ranOnceRef2.current = true;
    }
  }, [id, reset, navigate, getPaymentTermById, getRoutePath, initialized,hideLoading,showLoading,ranOnceRef2]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    showLoading()
    try {
      const formData = {
        ...data,
        active: isActive
      };
      
      let response;
      if (id) {
        // Update existing payment term
        response = await updatePaymentTerm(id, formData);
      } else {
        // Create new payment term
        response = await addPaymentTerm(formData);
      }
      
      if (response.status === 200 || response.status === 201) {
        toast.success(id ? 'Payment term updated successfully!' : 'Payment term created successfully!');
        navigate(getRoutePath('/payment-terms'));
      } else if (response.status === 422) {
        // Handle validation errors
        Object.keys(response.errors).forEach(field => {
          setError(field, { message: response.errors[field][0] });
          toast.error(response.errors[field][0]);
        });
      } else {
        toast.error('Error saving payment term');
      }
      hideLoading()
    } catch (error) {
      toast.error('Error saving payment term');
      console.error('Error:', error);
      hideLoading()
    } finally {
      hideLoading()
      setIsLoading(false);
    }
  };
 
  return (
    <div className="container mx-auto px-4 py-8">
      <Breadcrumb />
      <h1 className="text-2xl font-bold text-gray-900 mb-6">
        {id ? 'Edit Payment Term' : 'Create New Payment Term'}
      </h1>
      <div className="bg-white rounded-lg shadow-md p-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">Payment Term Name</label>
            <input
              type="text"
              {...register('name')}
              className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${
                errors.name ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
              }`}
              placeholder="Enter payment term name"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Due In Days</label>
            <input
              type="number"
              {...register('due_in_days')}
              min="0"
              className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${
                errors.due_in_days ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
              }`}
              placeholder="Enter number of days"
            />
            {errors.due_in_days && (
              <p className="mt-1 text-sm text-red-600">{errors.due_in_days.message}</p>
            )}
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Switch
                checked={isActive}
                onChange={setIsActive}
                className={`${
                  isActive ? 'bg-[#009efb]' : 'bg-gray-200'
                } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#009efb] focus:ring-offset-2`}
              >
                <span
                  className={`${
                    isActive ? 'translate-x-6' : 'translate-x-1'
                  } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                />
              </Switch>
              <span className="ml-3 text-sm font-medium text-gray-900">
                {isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate(getRoutePath('/payment-terms'))}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb] disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  {id ? 'Update Payment Term' : 'Create Payment Term'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentTermForm; 